import { forwardRef, useState } from "react";

import { useTranslations } from "next-intl";

import { latexGenerateLatexBundle } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import type { TeamItemUserPermissions } from "@/api";
import type { ComponentProps } from "react";

export const ExportToOverleafButton = forwardRef<
  HTMLButtonElement,
  { docId?: string; docPermissions: TeamItemUserPermissions } & ComponentProps<"button">
>(function ExportToOverleafButton({ docId, docPermissions, ...props }, ref) {
  const t = useTranslations("sidebar.downloadMenu");
  const [status, setStatus] = useState<"idle" | "downloading" | "error">("idle");
  const { auth, dialog } = useStore();

  const disabled = props.disabled || status === "downloading";

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e);

    if (!auth.isLoggedIn) {
      dialog.loginDialog.open();
      return;
    }

    if (status === "downloading" || !docId) return;

    // Track export to Overleaf button click
    if (docId) {
      trackEvent("export_to_overleaf_clicked", { doc_id: docId });
    }

    setStatus("downloading");

    if (!docPermissions.read) {
      dialog.requestPermissionDialog.open();
      return;
    }

    const zipRes = await latexGenerateLatexBundle({ docId, target: "overleaf" });

    const form = document.createElement("form");
    form.action = "https://www.overleaf.com/docs";
    form.method = "POST";
    form.target = "_blank";

    const engineInput = document.createElement("input");
    engineInput.type = "hidden";
    engineInput.name = "engine";
    engineInput.value = "xelatex";
    form.appendChild(engineInput);

    const snipInput = document.createElement("input");
    snipInput.type = "hidden";
    snipInput.name = "snip_uri";

    // convert zipRes to base64
    if (zipRes instanceof Blob) {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(zipRes);
      });
      snipInput.value = base64;
      form.appendChild(snipInput);

      document?.body.appendChild(form);
      form.submit();
      form.onsubmit = () => {
        document?.body.removeChild(form);
      };
    }

    setStatus("idle");
  };

  return (
    <button
      ref={ref}
      type="button"
      {...props}
      disabled={disabled}
      onClick={handleClick}
      className={twMerge(
        "w-full h-8.5 px-2.5 text-sm flex gap-2.5 items-center rounded-8 whitespace-nowrap tracking-tight",
        "sm:h-auto sm:py-2 sm:text-size-17",
        !disabled && "hover:bg-sidebar-button-active-bg data-[focus]:bg-sidebar-button-active-bg",
        disabled && "opacity-50",
        props.className,
      )}
    >
      {status === "downloading" ? (
        <Icon name="Spinner" className="animate-spin inline-block" width={24} />
      ) : (
        <Icon role="presentation" name="Overleaf" width={24} height={24} />
      )}
      <span>{t("openInOverleaf")}</span>
    </button>
  );
});
